// A plain strokes scoreboard for a round in progress, the way a golf scoreboard reads:
// position, who, how far round, and how they stand against par. See golfappspec.md §6
// for how the *points* side of a round works — this is deliberately not that.
import { getFormat } from "./formats/index.js";
import { allocateStrokes, holesInPlay } from "./handicap.js";
import { scorableHoles } from "./leaderboard.js";
import {
  buildScoreMap,
  computeHandicaps,
  grossOnHole,
  holeNumbersInPlay,
  listNames,
  parByHole,
  teamGrossOnHole,
} from "./formats/util.js";
import type { Course, Player, Round, Score, TeeSet } from "./types.js";

export interface ScoreboardRow {
  /** A player id, or a side's member ids sorted and joined. Stable across renders,
   * which is what keyed lists and the reordering animation need. */
  id: string;
  /** A player's name, or "Sam and Jo" for a side. */
  label: string;
  /** Playing handicap, allowance already applied. Null when the format doesn't play
   * off one — see the note on `sideRows`. */
  handicap: number | null;
  /** How many of this round's holes this row has a score for. */
  thru: number;
  /** Strokes the handicap gives back over the holes played — the difference between
   * `gross` and `net`, spelled out so a screen can show its working. */
  strokesReceived: number;
  /** Strokes actually taken over the holes played. Null before the first one. */
  gross: number | null;
  /** Gross less the strokes received. Equal to `gross` when the format has no
   * handicap in it. */
  net: number | null;
  /**
   * Gross against the par of *those same holes*.
   *
   * Not against the par of everything played so far by anyone, and not against the
   * full round: a card with a hole missing in the middle would otherwise read as
   * several shots better than it is, on the screen most likely to be believed.
   */
  toPar: number | null;
  /** Net against that same par. */
  netToPar: number | null;
  /** True once this row has a score on every hole of the round. */
  finished: boolean;
}

export interface RoundScoreboard {
  /** Best to-par first. Rows with nothing on the card sit at the bottom. */
  rows: ScoreboardRow[];
  /** Holes the whole field is through. */
  fieldThru: number;
  /** The hole the group is on — the first one the field hasn't finished. Null once
   * every hole is in. */
  currentHole: number | null;
  /** Scramble and alternate shot, where a row is a side rather than a person. */
  bySide: boolean;
  /**
   * Which figure the round is actually decided on, and therefore what the positions
   * above are in. Taken from the format's own `uses.handicapAllowance`, the same flag
   * `shared/explain.ts` reads to choose a basis — so a scoreboard can never disagree
   * with the breakdown screens about whether a round is a net one.
   *
   * A screen showing these rows is expected to say which it is. "+8" means nothing
   * until you know whether the handicap has already come off it.
   */
  basis: "net" | "gross";
  /** Holes this round plays, so a caller can say "14 of 18". */
  holesTotal: number;
}

/** Rounds are configured before their course is picked, and a course is fetched
 * asynchronously even from the cache — so "no tee set" is an ordinary state, not a
 * fault, and the caller draws nothing rather than an empty grid. */
export function buildScoreboard(
  round: Round,
  course: Course,
  players: Player[],
  scores: Score[],
): RoundScoreboard | null {
  const teeSet = course.teeSets.find((t) => t.name === round.teeSet);
  if (!teeSet) return null;

  const format = getFormat(round.formatId);
  const holes = holeNumbersInPlay(round, teeSet);
  const pars = parByHole(teeSet, round);
  // buildScoreMap has no notion of a round, so this filter is not optional: without it
  // a player's hole 3 in another round would land on this one's hole 3.
  const scoreMap = buildScoreMap(scores.filter((s) => s.roundId === round.id));
  const handicaps = computeHandicaps(round, teeSet, players);
  const playsOffHandicap = Boolean(format.uses.handicapAllowance);

  const done = scorableHoles(round, course, players, scores);
  const fieldThru = done.length;
  const finishedByField = new Set(done);
  const currentHole = holes.find((h) => !finishedByField.has(h)) ?? null;

  const bySide = format.entry === "perTeam";
  const basis: "net" | "gross" = playsOffHandicap ? "net" : "gross";
  const rows = bySide
    ? sideRows(round, teeSet, holes, pars, scoreMap, handicaps, players, playsOffHandicap)
    : playerRows(holes, pars, scoreMap, handicaps, players, playsOffHandicap);

  return {
    rows: sortRows(rows, basis),
    fieldThru,
    currentHole,
    bySide,
    basis,
    holesTotal: holes.length,
  };
}

type Tallied = Pick<
  ScoreboardRow,
  "thru" | "gross" | "net" | "toPar" | "netToPar" | "strokesReceived"
>;

/**
 * Adds up a card over the holes it actually has.
 *
 * Strokes received are counted the same way — only on holes that were played — so
 * gross, net and the two to-par figures are all describing the same set of holes. A
 * net total that gave back the handicap for holes nobody has reached yet would put a
 * player under par before they'd earned it.
 */
function tally(
  holes: number[],
  pars: Map<number, number>,
  strokesOn: (hole: number) => number | null,
  receivedOn: (hole: number) => number,
): Tallied {
  let thru = 0;
  let gross = 0;
  let par = 0;
  let received = 0;
  for (const hole of holes) {
    const strokes = strokesOn(hole);
    if (strokes == null) continue;
    thru += 1;
    gross += strokes;
    par += pars.get(hole) ?? 0;
    received += receivedOn(hole);
  }
  if (thru === 0) {
    return { thru: 0, gross: null, net: null, toPar: null, netToPar: null, strokesReceived: 0 };
  }
  return {
    thru,
    gross,
    net: gross - received,
    toPar: gross - par,
    netToPar: gross - received - par,
    strokesReceived: received,
  };
}

function playerRows(
  holes: number[],
  pars: Map<number, number>,
  scoreMap: Map<string, number>,
  handicaps: ReturnType<typeof computeHandicaps>,
  players: Player[],
  playsOffHandicap: boolean,
): ScoreboardRow[] {
  return players.map((player) => {
    const strokesByHole = handicaps.get(player.id)?.strokesByHole;
    const counted = tally(
      holes,
      pars,
      (hole) => grossOnHole(scoreMap, player.id, hole),
      // A format with no handicap in it gives nothing back, so net tracks gross.
      (hole) => (playsOffHandicap ? (strokesByHole?.get(hole) ?? 0) : 0),
    );
    return {
      id: player.id,
      label: player.name,
      handicap: playsOffHandicap ? (handicaps.get(player.id)?.playingHandicap ?? null) : null,
      ...counted,
      finished: counted.thru === holes.length && holes.length > 0,
    };
  });
}

/**
 * One row per side, for the two formats where the side plays a single ball.
 *
 * A row per player would be wrong twice over: the shared score is recorded against
 * one canonical member, so that player would be shown the whole side's total and
 * their partner nothing at all.
 *
 * Sides come from the round's segments, and segments can re-form mid-round — so this
 * walks the holes in order and keys each side on its membership. A pairing that
 * changes is genuinely a different side and gets its own row.
 *
 * Segment bounds are POSITIONS within the round's hole set while scores are keyed by
 * absolute hole NUMBER. On a back nine those differ by nine, and conflating them is
 * the standing trap in this codebase (see `holesInSegment`).
 */
function sideRows(
  round: Round,
  teeSet: TeeSet,
  holes: number[],
  pars: Map<number, number>,
  scoreMap: Map<string, number>,
  handicaps: ReturnType<typeof computeHandicaps>,
  players: Player[],
  playsOffHandicap: boolean,
): ScoreboardRow[] {
  const nameOf = (id: string) => players.find((p) => p.id === id)?.name ?? id;
  const sides = new Map<string, { members: string[]; holes: number[] }>();

  holes.forEach((hole, index) => {
    const position = index + 1;
    const segment = round.segments.find((s) => position >= s.startHole && position <= s.endHole);
    if (!segment) return;
    for (const team of segment.teams) {
      if (team.length === 0) continue;
      const id = [...team].sort().join("+");
      const side = sides.get(id) ?? { members: team, holes: [] };
      side.holes.push(hole);
      sides.set(id, side);
    }
  });

  const roundHoles = holesInPlay(teeSet.holes, round.holeSet);

  return [...sides.entries()].map(([id, side]) => {
    // Summed because each member's figure already has the round's allowance applied,
    // and a side's mark in alternate shot is the combined one. Scramble sets this flag
    // false and derives its own team handicap, so it shows none rather than a number
    // nobody is playing off.
    const combined = playsOffHandicap
      ? side.members.reduce((sum, m) => sum + (handicaps.get(m)?.playingHandicap ?? 0), 0)
      : null;
    // Allocated from the combined figure rather than by adding up the members' own
    // per-hole strokes. Those are not the same distribution: two players on 10 and 14
    // both take a stroke on the ten hardest holes, which double-counts them, where a
    // side on 24 takes one everywhere and a second on the six hardest. Over a full
    // round the totals agree; over the holes played so far they do not.
    const received = combined == null ? null : allocateStrokes(combined, roundHoles);
    const counted = tally(
      side.holes,
      pars,
      (hole) => teamGrossOnHole(scoreMap, side.members, hole),
      (hole) => received?.get(hole) ?? 0,
    );
    return {
      id,
      label: listNames(side.members.map(nameOf)),
      handicap: combined,
      ...counted,
      finished: counted.thru === side.holes.length && side.holes.length > 0,
    };
  });
}

/** The figure this round is decided on — the one the board is ordered and numbered by. */
export function standingFigure(row: ScoreboardRow, basis: "net" | "gross"): number | null {
  return basis === "net" ? row.netToPar : row.toPar;
}

/**
 * Best score first, with anyone yet to hole out at the bottom — a row on nothing is not
 * leading, which is what sorting a null as zero would claim.
 *
 * Ordered on the round's own basis: in a net format the person on top of the board is
 * the one winning it, not whoever is striking it best, and those are routinely
 * different people.
 */
function sortRows(rows: ScoreboardRow[], basis: "net" | "gross"): ScoreboardRow[] {
  return [...rows].sort((a, b) => {
    const av = standingFigure(a, basis);
    const bv = standingFigure(b, basis);
    if (av == null && bv == null) return a.label.localeCompare(b.label);
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av !== bv) return av - bv;
    // Level with each other but one of them has played more holes: they've held that
    // score for longer, so they're ahead of it.
    if (a.thru !== b.thru) return b.thru - a.thru;
    return a.label.localeCompare(b.label);
  });
}

/** Shared placements, so two players level on the same number of holes read as joint. */
export function scoreboardPlacements(
  rows: ScoreboardRow[],
  basis: "net" | "gross" = "gross",
): (number | null)[] {
  let placement = 0;
  let previous: string | null = null;
  return rows.map((row, index) => {
    const value = standingFigure(row, basis);
    if (value == null) return null;
    const key = `${value}:${row.thru}`;
    if (key !== previous) {
      placement = index + 1;
      previous = key;
    }
    return placement;
  });
}
